import React from 'react';

import {
  ErrorView as Error,
  NoPermissions,
  NotFound,
  PathBuilder,
  UserPermissions,
  rolesChildren,
} from '@gasbi/shared';
import { OrganizationPages } from '@gasbi/shared/web/Organization/pages';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useParams,
} from 'react-router-dom';

import { Pages } from '@/pages';
import { makeSSOIntegrationsRoute } from '@/pages/SSOIntegrations/util';
import { allUsersDetailsRoutes } from '@/pages/UsersDetails/routes';
import {
  AuthGuard,
  AuthInitProvider,
  OrganizationApiContextWrapper,
} from '@/providers';
import { OnboardingGate } from '@/providers/OnboardingGate';

const AuthCallback = React.lazy(() => import('@/pages/auth/AuthCallback'));
const NotAuthorizedPage = React.lazy(
  () => import('@/pages/auth/NotAuthorizedPage'),
);
const ChangePassword = React.lazy(() => import('@/pages/auth/ChangePassword'));
const MyOrganization = React.lazy(() => import('@/pages/MyOrganization'));
const AdminOrganization = React.lazy(() => import('@/pages/AdminOrganization'));
const MyOrganizationInformation = React.lazy(
  () => import('@/pages/MyOrganization/views/Information'),
);
const MyOrganizationEmployeeGroups = React.lazy(
  () => import('@/pages/MyOrganization/views/EmployeeGroups'),
);
const MyOrganizationEmployees = React.lazy(
  () => import('@/pages/MyOrganization/views/Employees'),
);
const EmployeeDetails = React.lazy(() => import('@/pages/EmployeeDetails'));
const Users = React.lazy(() => import('@/pages/Users'));
const UsersDetails = React.lazy(() => import('@/pages/UsersDetails'));
const ServiceEmployeeDetails = React.lazy(
  () => import('@/pages/ServiceEmployeeDetails'),
);
const Onboarding = React.lazy(() => import('@/pages/Onboarding'));
const MyOrganizations = React.lazy(
  () => import('@/pages/MyOrganizations/MyOrganizationsRoute'),
);
const MyOrganizationsList = React.lazy(() => import('@/pages/MyOrganizations'));
const ReferenceBooks = React.lazy(() => import('@/pages/ReferenceBooks'));
const ReferenceBook = React.lazy(() => import('@/pages/ReferenceBook'));
const GasDistributionNetworkOperators = React.lazy(
  () => import('@/pages/ReferenceBook/GasDistributionNetworkOperators'),
);
const ConsumerCategories = React.lazy(
  () => import('@/pages/ReferenceBook/ConsumerCategories'),
);

const WorkingPeriods = React.lazy(() => import('@/pages/WorkingPeriods'));
const Counterparties = React.lazy(
  () => import('@/pages/Counterparties/CounterpartiesDashboard'),
);
const SetupApiKeys = React.lazy(() => import('@/pages/SetupApiKeys'));
const ApiKeys = React.lazy(() => import('@/pages/ApiKeys'));
const LlmProviderConfiguration = React.lazy(
  () => import('@/pages/LlmProviderConfiguration'),
);
const LlmUsage = React.lazy(() => import('@/pages/LlmUsage'));
const BiDashboards = React.lazy(() => import('@/pages/BiDashboards'));
const McpServers = React.lazy(() => import('@/pages/McpServers'));
const McpServerDetails = React.lazy(
  () => import('@/pages/McpServers/McpServerDetails'),
);
const McpConnections = React.lazy(() => import('@/pages/McpConnections'));
const Agents = React.lazy(() => import('@/pages/Agents'));
const AgentDetails = React.lazy(() => import('@/pages/AgentDetails'));
const AgentRoles = React.lazy(() => import('@/pages/AgentRoles'));
const AgentRoleDetails = React.lazy(() => import('@/pages/AgentRoleDetails'));
const Skills = React.lazy(() => import('@/pages/Skills'));
const SkillDetails = React.lazy(() => import('@/pages/SkillDetails'));
const KnowledgeCollections = React.lazy(
  () => import('@/pages/Knowledge/KnowledgeCollectionsPage'),
);
const KnowledgeCollection = React.lazy(
  () => import('@/pages/Knowledge/KnowledgeCollectionPage'),
);

// Preserves deep links to the legacy /organizations/admin/* URLs.
const AdminServiceRedirect = () => {
  const { '*': tail = '' } = useParams();
  return <Navigate to={`${Pages.ADMIN_SERVICE}/${tail}`} replace />;
};

const rolesRoute = (isMainOrganizationView: boolean) => ({
  element: (
    <OrganizationApiContextWrapper
      isMainOrganizationView={isMainOrganizationView}
    >
      <Outlet />
    </OrganizationApiContextWrapper>
  ),
  children: rolesChildren(isMainOrganizationView),
});

const protectedRoutes = [
  {
    path: Pages.ROOT,
    element: <Navigate to={Pages.AUTH_CALLBACK} replace />,
  },
  {
    path: Pages.AUTH_CALLBACK,
    element: <AuthCallback />,
  },
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedPage />,
  },
  {
    path: Pages.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  {
    path: Pages.SETUP_API_KEYS,
    element: <SetupApiKeys />,
  },
  {
    element: <MyOrganizations />,
    children: [
      {
        path: Pages.ORGANIZATIONS,
        children: [
          { index: true, element: <MyOrganizationsList /> },
          {
            path: Pages.ADMIN_ORGANIZATION_PART,
            element: <Navigate to={Pages.ADMIN_SERVICE} replace />,
          },
          {
            path: `${Pages.ADMIN_ORGANIZATION_PART}/*`,
            element: <AdminServiceRedirect />,
          },
          {
            path: ':orgId',
            element: <MyOrganization />,
            children: [
              { index: true, element: <MyOrganizationInformation /> },
              {
                path: Pages.EMPLOYEES_PART,
                element: <MyOrganizationEmployeeGroups />,
              },
            ],
          },
          {
            path: `:orgId/${Pages.EMPLOYEES_PART}/user/:identityId`,
            element: <EmployeeDetails />,
          },
          {
            path: `:orgId/${Pages.EMPLOYEES_PART}/services/:serviceId/:employeeId`,
            element: <ServiceEmployeeDetails />,
          },
          {
            path: `:orgId/${OrganizationPages.ROLES_PART}`,
            ...rolesRoute(false),
          },
        ],
      },
      {
        path: Pages.ADMIN_SERVICE,
        element: <AdminOrganization />,
        children: [
          {
            index: true,
            element: <Navigate to={Pages.EMPLOYEES_PART} replace />,
          },
          {
            path: Pages.EMPLOYEES_PART,
            element: <MyOrganizationEmployees />,
          },
        ],
      },
      {
        path: `${Pages.ADMIN_SERVICE}/${Pages.EMPLOYEES_PART}/:employeeId`,
        element: <EmployeeDetails />,
      },
      {
        path: `${Pages.ADMIN_SERVICE}/${OrganizationPages.ROLES_PART}`,
        ...rolesRoute(true),
      },
      { path: Pages.REFERENCE_BOOKS, element: <ReferenceBooks /> },
      {
        path: Pages.USERS,
        element: <Users />,
        handle: {
          keepAlive: true,
          permissions: UserPermissions.AdministrationUserRead,
        },
      },
      {
        path: `${Pages.USERS}/:identityId`,
        element: <UsersDetails />,
        handle: {
          permissions: UserPermissions.AdministrationUserRead,
        },
        children: allUsersDetailsRoutes,
      },
      {
        path: `${Pages.REFERENCE_BOOKS}/GasDistributionNetworkOperator`,
        element: <GasDistributionNetworkOperators />,
      },
      {
        path: PathBuilder.absolute()
          .page(Pages.REFERENCE_BOOKS)
          .page('ConsumerCategory')
          .build(),
        element: <ConsumerCategories />,
      },
      { path: `${Pages.REFERENCE_BOOKS}/:type`, element: <ReferenceBook /> },
      { path: Pages.WORKING_PERIODS, element: <WorkingPeriods /> },
      { path: Pages.API_KEYS, element: <ApiKeys /> },
      {
        path: Pages.LLM_PROVIDER_CONFIGURATION,
        element: <LlmProviderConfiguration />,
      },
      {
        path: Pages.LLM_USAGE,
        element: <LlmUsage />,
        handle: {
          permissions:
            UserPermissions.AdministrationLlmProviderConfigurationRead,
        },
      },
      { path: Pages.BI_DASHBOARDS, element: <BiDashboards /> },
      {
        path: Pages.MCP_SERVERS,
        element: <McpServers />,
        handle: {
          permissions: UserPermissions.AdministrationMcpConnectionsRead,
        },
      },
      {
        path: `${Pages.MCP_SERVERS}/:id`,
        element: <McpServerDetails />,
        handle: {
          permissions: UserPermissions.AdministrationMcpConnectionsRead,
        },
      },
      {
        path: Pages.MCP_CONNECTIONS,
        element: <McpConnections />,
        handle: {
          permissions: UserPermissions.AdministrationMcpConnectionsRead,
        },
      },
      {
        path: Pages.AGENTS,
        element: <Agents />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: `${Pages.AGENTS}/:id`,
        element: <AgentDetails />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: Pages.AGENT_ROLES,
        element: <AgentRoles />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: `${Pages.AGENT_ROLES}/:id`,
        element: <AgentRoleDetails />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: Pages.SKILLS,
        element: <Skills />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: `${Pages.SKILLS}/:id`,
        element: <SkillDetails />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: Pages.KNOWLEDGE,
        element: <KnowledgeCollections />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      {
        path: `${Pages.KNOWLEDGE}/:id`,
        element: <KnowledgeCollection />,
        handle: {
          permissions: UserPermissions.AdministrationAgentsRead,
        },
      },
      { path: Pages.COUNTERPARTIES, element: <Counterparties /> },
      makeSSOIntegrationsRoute(),
    ],
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const allRoutes = [
  {
    element: <OnboardingGate />,
    children: [
      {
        path: Pages.ONBOARDING,
        element: <Onboarding />,
      },
      {
        element: (
          <AuthInitProvider>
            <AuthGuard />
          </AuthInitProvider>
        ),
        children: protectedRoutes,
      },
    ],
  },
];

export const getRoutes = () =>
  createBrowserRouter(
    allRoutes.map(route => ({ ...route, errorElement: <Error /> })),
  );
